/** @jsxImportSource @emotion/react */

import { useCallback, useEffect, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Modal from '@mui/material/Modal'
import InsertLinkIcon from '@mui/icons-material/InsertLink'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import Table, { TableProps } from 'components/Table'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { PlatformSlug, TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { BULK_UPDATE_CHAPTER_NOTES, GET_ALL_CHAPTER_NOTES } from 'queries/chapterNotes'
import { OrderDirection } from '__generatedTypes__/globalTypes'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import {
  findAllChapterNotes,
  findAllChapterNotes_findAllChapterNotes_items as ChapterNote,
  findAllChapterNotesVariables,
} from '__generatedTypes__/findAllChapterNotes'
import { bulkUpdateChapterNotes, bulkUpdateChapterNotesVariables } from '__generatedTypes__/bulkUpdateChapterNotes'
import TableButtons from 'components/TableButtons'
import { cmsRoutes } from 'routes/cmsRoutes'
import * as commonClasses from 'components/shared/GroupCards/commonStyles'
import { ManageModal } from '../ManageModal'
import * as classes from './styles'

type ChapterNotesProps = {
  platform: Platform
}

export const ChapterNotesList = withPlatformCheck(({ platform }: ChapterNotesProps) => {
  const navigate = useNavigate()
  const { query, mutate } = useApolloClient()
  const [chapterNotes, setChapterNotes] = useState<ChapterNote[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [editableChapterNote, setEditableChapterNote] = useState<Partial<ChapterNote> | null>(null)

  const loadChapterNotes = useCallback(async () => {
    try {
      const { data } = await query<findAllChapterNotes, findAllChapterNotesVariables>({
        query: GET_ALL_CHAPTER_NOTES,
        variables: {
          params: {
            take: TAKE_ALL_ITEMS_COUNT,
            order: {
              order: OrderDirection.ASC,
            },
          },
        },
        fetchPolicy: 'network-only',
      })
      const items = data.findAllChapterNotes.items
      if (items.length && items[items.length - 1].order !== items.length - 1) {
        await handleSort(items)
        setChapterNotes(items.map((item, order) => ({ ...item, order })))
      } else {
        setChapterNotes(items)
      }
      setIsLoaded(true)
    } catch (e) {
      toast.error('Failed to load Chapter Notes')
    }
  }, [])

  useEffect(() => {
    loadChapterNotes()
  }, [])

  const handleSort = useCallback(async (chapterNotesUpd: ChapterNote[]) => {
    setChapterNotes(chapterNotesUpd)

    try {
      await mutate<bulkUpdateChapterNotes, bulkUpdateChapterNotesVariables>({
        mutation: BULK_UPDATE_CHAPTER_NOTES,
        variables: {
          params: chapterNotesUpd.map(({ id }, order) => ({ id, order })),
        },
      })
      toast.success('Chapter Notes order updated successfully')
    } catch (e) {
      toast.error('Failed to update Chapter Notes order')
    }
  }, [])

  const columns: TableProps<ChapterNote>['columns'] = [
    {
      title: 'Title',
      renderCell: (note) => (
        <Typography css={commonClasses.longText} variant="body2" data-testid="card-title">
          {note.title}
        </Typography>
      ),
    },
    {
      title: 'URL',
      renderCell: (note) => (
        <IconButton size="small" href={note.link} target="_blank" css={classes.link}>
          <InsertLinkIcon />
        </IconButton>
      ),
      flex: '0 1 100px',
    },
    {
      renderCell: (note) => (
        <EditIcon
          css={commonClasses.editIcon}
          onClick={() => setEditableChapterNote(note)}
          data-testid="edit-card-btn"
        />
      ),
      flex: '0 1 29px',
    },
  ]

  useEffect(() => {
    if (platform.slug !== PlatformSlug.CasperBooster) {
      navigate(`/${String(platform.slug)}/${cmsRoutes.NOT_FOUND}`)
    }
  }, [])

  if (platform.slug !== PlatformSlug.CasperBooster) {
    return null
  }

  if (!isLoaded) {
    return <PageLoader />
  }

  return (
    <div>
      <Table columns={columns} items={chapterNotes} onSort={handleSort} isSortable />
      <TableButtons
        itemName="Chapter Note"
        onAddItem={() => setEditableChapterNote({ order: chapterNotes.length })}
        onCancel={() => setEditableChapterNote(null)}
      />
      <Modal open={!!editableChapterNote} onClose={() => setEditableChapterNote(null)}>
        <ManageModal
          initialValues={editableChapterNote ?? {}}
          isEditMode={!!editableChapterNote?.id}
          onClose={() => setEditableChapterNote(null)}
          onSubmit={async () => {
            await loadChapterNotes()
            setEditableChapterNote(null)
          }}
        />
      </Modal>
    </div>
  )
})
